/**
 * Selector de color del Diseñador: el selector visual del navegador (<input type="color">) MÁS un campo de
 * texto donde se puede escribir o pegar un código hexadecimal.
 *
 *  - El texto acepta #RRGGBB o #RGB, con o sin "#", en mayúsculas o minúsculas (ver utils/colorHex.js).
 *  - En cuanto el texto es un color válido se aplica (onCambiar) sin esperar al Enter ni al blur; mientras se
 *    está escribiendo no se muestra ningún error (un "#1a9" a medio escribir es un 3 dígitos válido que se
 *    aplica y se pisa cuando termina de escribirse "#1a9b8e").
 *  - Si el texto no es un color (letras que no son hex, más de 6 dígitos, o incompleto al salir del campo)
 *    NO se aplica nada: el color anterior queda tal cual, el campo se marca en rojo con un aviso y se
 *    corrige escribiendo de nuevo, con Escape (vuelve al color vigente) o eligiendo un color con el selector.
 *  - `mixto`: varios elementos con colores distintos. No se muestra ningún color real: el cuadro es un
 *    rayado, el campo está vacío con el texto "Mixto", y recién al elegir o escribir un color se aplica a todos.
 *
 * `valor` es "#rrggbb" (o null cuando es mixto). onCambiar recibe siempre "#rrggbb" en minúsculas.
 */
import React, { useEffect, useRef, useState } from 'react';
import { normalizarHex, esPrefijoDeHex } from '../utils/colorHex';

// Valor que se le da al selector visual cuando no hay un color real que mostrar (mixto): nunca se ve.
const COLOR_NEUTRO = '#808080';

const SelectorColor = ({ valor, onCambiar, mixto = false, etiqueta = 'Color' }) => {
  // Texto tipeado mientras el campo está en uso (null = se muestra el color vigente).
  const [borrador, setBorrador] = useState(null);
  const [saliDelCampo, setSaliDelCampo] = useState(false);
  const ultimoEmitido = useRef(null);

  // Si el color cambia desde afuera (otro elemento, el selector visual, un cambio en bloque...), el texto
  // que había quedado a medias se descarta. Si lo cambió este mismo campo, se conserva lo que se tipeó.
  useEffect(() => {
    if (valor !== ultimoEmitido.current) {
      setBorrador(null);
      setSaliDelCampo(false);
    }
  }, [valor, mixto]);

  const vigente = mixto ? null : normalizarHex(valor);
  const textoVigente = mixto ? '' : (vigente ? vigente.toUpperCase() : String(valor ?? ''));
  const texto = borrador !== null ? borrador : textoVigente;
  const hayTexto = borrador !== null && borrador.trim() !== '';
  const esValido = hayTexto && normalizarHex(borrador) !== null;
  // Error: lo escrito no puede ser un color (letras no hex, demasiados dígitos) o quedó incompleto al salir.
  const hayError = hayTexto && !esValido && (!esPrefijoDeHex(borrador) || saliDelCampo);

  const emitir = (hex) => {
    ultimoEmitido.current = hex;
    onCambiar(hex);
  };

  const alCambiarTexto = (e) => {
    const t = e.target.value;
    setBorrador(t);
    setSaliDelCampo(false);
    const hex = normalizarHex(t);
    if (hex) emitir(hex);
  };

  const alSalirDelCampo = () => {
    if (borrador === null) return;
    if (!hayTexto || esValido) {
      // Vacío o válido: se vuelve a mostrar el color vigente, ya normalizado (#RRGGBB en mayúsculas).
      setBorrador(null);
      setSaliDelCampo(false);
    } else {
      setSaliDelCampo(true);
    }
  };

  const alApretarTecla = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setBorrador(null);
      setSaliDelCampo(false);
      e.target.blur();
    }
  };

  const alElegirConElSelectorVisual = (e) => {
    setBorrador(null);
    setSaliDelCampo(false);
    emitir(e.target.value);
  };

  return (
    <div className="selector-color">
      <div className="disenador-color-picker">
        {mixto ? (
          // Varios colores distintos: un cuadro rayado (no es ningún color) que abre igual el selector visual.
          <label className="selector-color-mixto" title="Mixto: elegí un color para aplicarlo a todos">
            <input type="color" value={COLOR_NEUTRO} onChange={alElegirConElSelectorVisual} aria-label={`${etiqueta}: elegir con el selector`} />
          </label>
        ) : (
          <input
            type="color"
            value={vigente || COLOR_NEUTRO}
            onChange={alElegirConElSelectorVisual}
            aria-label={`${etiqueta}: elegir con el selector`}
          />
        )}
        <input
          type="text"
          className={`padron-input disenador-color-value selector-color-texto ${hayError ? 'invalido' : ''}`}
          value={texto}
          placeholder={mixto ? 'Mixto' : '#RRGGBB'}
          onChange={alCambiarTexto}
          onBlur={alSalirDelCampo}
          onKeyDown={alApretarTecla}
          maxLength={20}
          spellCheck={false}
          autoComplete="off"
          aria-label={`${etiqueta}: código hexadecimal`}
          aria-invalid={hayError}
        />
      </div>
      {hayError && (
        <span className="form-hint selector-color-error" role="alert">
          Código no válido, no se aplicó. Escribí #RRGGBB o #RGB (por ejemplo #1a9b8e o #1a9). Esc vuelve al color actual.
        </span>
      )}
      {mixto && !hayTexto && (
        <span className="form-hint">Los elementos tienen colores distintos (Mixto). Escribí un código o elegí un color para igualarlos.</span>
      )}
    </div>
  );
};

export default SelectorColor;
